import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/atoms";
import { useTranslation } from "@/core/i18n";
import { getMultiPlatformPatientAppHostByPlatformId } from "@/api/orval/multi-platform/multi-platform";
import { getCurrentPlatform, getGetCurrentPlatformQueryKey } from "@/api/orval/platform/platform";
import {
  setAnonymousBrowsingAllowed,
  setAnonymousCheckoutAllowed,
  setBrowseLocationId,
} from "@/modules/anonymousBrowsing";
import { MobileViewportContainer } from "@/modules/onboarding/components/MobileViewportContainer/MobileViewportContainer";
import { OnboardingBackButton } from "@/modules/onboarding/components/OnboardingBackButton/OnboardingBackButton";
import { PlatformSearchModal } from "@/modules/onboarding/components/PlatformSearchModal/PlatformSearchModal";
import { storage } from "@/modules/storage";
import { useAppState } from "contexts/AppContext";
import { practiceApiUrl } from "hooks/practiceApiUrl";
import { queryClient, ThemeContext } from "pages/Root";

/**
 * "Skip login for now" on the native app shell. The shell runs on the bare repeatmd.app host, which belongs to no
 * practice, so a guest first picks one. Picking points the app at that practice's host — exactly what signed-in
 * platform switching does, minus the token — and anonymous browsing takes over from there.
 *
 * A practice with anonymous browsing off has nothing a guest can see, so the visitor lands on that practice's
 * sign-up instead.
 */
export default function V3GuestPracticePicker() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const appState = useAppState();
  const { resetTheme } = useContext(ThemeContext);
  const [showSearchModal, setShowSearchModal] = useState(true);
  const [hasOpenError, setHasOpenError] = useState(false);
  const [isOpening, setIsOpening] = useState(false);

  const handlePlatformSelect = async (platformId: string) => {
    // Each attempt swaps the global API host and clears the shared cache, so a second tap must not overlap it.
    if (isOpening) {
      return;
    }
    setIsOpening(true);
    setHasOpenError(false);
    // Raw storage, not appState: an empty value must stay empty so the shell keeps its env default host.
    const previousApiUrl = storage.getItem("baseApiUrl") ?? "";
    const previousPlatformId = storage.getItem("platformId") ?? "";

    try {
      const { patientAppHost } = await getMultiPlatformPatientAppHostByPlatformId(platformId);

      appState.setApiUrl(practiceApiUrl(patientAppHost));
      appState.setPlatformId(platformId);
      // A branch picked at another practice means nothing here.
      setBrowseLocationId(null);
      queryClient.clear();

      const platform = await queryClient.fetchQuery({
        queryKey: getGetCurrentPlatformQueryKey(),
        queryFn: () => getCurrentPlatform(),
      });
      const { platformSettings } = platform;
      if (!platformSettings) {
        throw new Error(`Practice ${platformId} returned no settings`);
      }
      // The router applied these once at start-up, against the bare shell host.
      setAnonymousBrowsingAllowed(platformSettings.anonymousBrowsingEnabled);
      setAnonymousCheckoutAllowed(platformSettings.anonymousCheckoutEnabled);
      resetTheme();

      navigate(platformSettings.anonymousBrowsingEnabled ? "/home" : "/signin", { replace: true });
    } catch {
      // The generated client already logs the failed request through the sanitized API logger.
      console.error("Failed to open practice for guest browsing");
      // Leave the shell where it was, so the visitor can pick another practice.
      appState.setApiUrl(previousApiUrl);
      appState.setPlatformId(previousPlatformId);
      queryClient.clear();
      setHasOpenError(true);
      setShowSearchModal(false);
    } finally {
      setIsOpening(false);
    }
  };

  return (
    <>
      <PlatformSearchModal
        open={showSearchModal}
        onClose={() => setShowSearchModal(false)}
        onPlatformSelect={handlePlatformSelect}
      />

      <MobileViewportContainer header={<OnboardingBackButton />}>
        <div className="flex flex-col items-center justify-center gap-6 px-8 py-16">
          {hasOpenError && (
            <div className="text-center text-destructive" role="alert">
              {t("v3GuestPracticePicker.openError")}
            </div>
          )}
          <Button
            disabled={isOpening}
            fullWidth
            onClick={() => setShowSearchModal(true)}
            style="secondary"
          >
            {t("platformSearchModal.title")}
          </Button>
        </div>
      </MobileViewportContainer>
    </>
  );
}
